"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaCode, FaCloud, FaMobile, FaLightbulb } from "react-icons/fa";
import { useEffect, useState } from "react";
import HeroSection from "../../components/common/HeroSection";

const services = [
  {
    icon: <FaCode className="text-4xl" />,
    title: "Web Development",
    description: "Custom web applications and solutions built with modern technologies and best practices.",
    features: ["Responsive Design", "Full-Stack Development", "E-commerce Solutions", "CMS Integration"],
    link: "/services/web-development",
    color: "from-blue-500 to-indigo-500"
  },
  {
    icon: <FaMobile className="text-4xl" />,
    title: "Mobile Apps",
    description: "Native and cross-platform mobile applications for iOS and Android platforms.",
    features: ["iOS & Android Apps", "Cross-Platform Solutions", "UI/UX Design", "App Maintenance"],
    link: "/services/mobile-apps",
    color: "from-indigo-500 to-purple-500"
  },
  {
    icon: <FaCloud className="text-4xl" />,
    title: "Cloud Solutions",
    description: "Scalable cloud infrastructure and migration services for modern businesses.",
    features: ["Cloud Migration", "AWS & Azure", "DevOps", "Cloud Security"],
    link: "/services/cloud-solutions",
    color: "from-purple-500 to-pink-500"
  },
  {
    icon: <FaLightbulb className="text-4xl" />,
    title: "Consulting",
    description: "Strategic technology consulting to help businesses innovate and grow.",
    features: ["Tech Strategy", "Digital Transformation", "Process Optimization", "IT Consulting"],
    link: "/services/consulting",
    color: "from-pink-500 to-red-500"
  }
];

// Add this component before the ServicesPage component
const AnimatedBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute inset-0 opacity-30"
        style={{
          background: "radial-gradient(circle at center, transparent 0%, #f3f4f6 70%)",
        }}
        animate={{
          x: mousePosition.x * 20,
          y: mousePosition.y * 20,
        }}
      />
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full mix-blend-multiply filter blur-xl"
          animate={{
            x: [0, 30, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            delay: i * 2,
            ease: "easeInOut",
          }}
          style={{
            width: "40%",
            height: "40%",
            background: `linear-gradient(45deg, ${services[i % 4].color.split(" ")[1]}, ${services[i % 4].color.split(" ")[3]})`,
            left: `${(i * 25) % 75}%`,
            top: `${(i * 20) % 60}%`,
          }}
        />
      ))}
    </div>
  );
};

// Update the main section's background
export default function ServicesPage() {
  return (
    <div className="min-h-screen relative bg-white overflow-hidden">
      <AnimatedBackground />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <HeroSection
          title="Our Services"
          description="End-to-end technology solutions to help your business thrive in the digital age."
          showPattern={true}
        />
      </section>

      {/* Services Grid continues... */}
      <div className="container mx-auto px-4 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={service.link}>
                <div className="group h-full p-8 rounded-3xl bg-white/50 backdrop-blur-sm border border-gray-100
                  hover:bg-white hover:shadow-xl transition-all duration-500 hover:scale-[1.02]">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${service.color} 
                    flex items-center justify-center text-white mb-6
                    group-hover:scale-110 transition-transform duration-500`}>
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center text-indigo-600 font-medium group/link">
                    <span>Learn More</span>
                    <svg className="w-5 h-5 ml-2 transform group-hover/link:translate-x-1 transition-transform" 
                      fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12 text-center text-white"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Let's Build Something Amazing Together</h2>
          <p className="text-xl mb-8 text-white/90">
            Ready to transform your business with cutting-edge technology solutions?
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-indigo-600 px-8 py-4 rounded-full text-lg font-semibold
              hover:shadow-lg transition-all duration-300"
          >
            Schedule a Consultation
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}